// Record Receipt drawer: the pure rules (no React, no network). The POST bodies are the SAME the old ReceiptPanel built (field for field),
// so the server (POST /goods-receipts: ETA/status/storage/over-receipt/period-lock/tie-out guards) stays the one authority; this file only
// pre-checks, plans the sequence of posts and words the soft notes. Order line ETA = purchase_detail.expected_receipt_date (per line).
import { dayDiff } from '../../Sales/dispatchEta.mjs';

const EPS = 0.0001;
export const num = v => parseFloat(v) || 0;
// the raw category KEY decides birds (a label never does: 674/666)
export const isBirdLine = l => String(l?.category || '').toLowerCase() === 'birds';
// what can still be received on a line = the engine's remaining (what the server checks), else ordered - received
export const lineOpen = l => Math.max(0, l?.qty_open != null ? num(l.qty_open) : num(l?.qty_ordered) - num(l?.qty_received));
export const receivableLines = (lines, scopeLineId = null) => (lines || []).filter(l => (scopeLineId ? l.id === scopeLineId : lineOpen(l) > EPS));
export const rawCost = l => l?.initial_unit_price ?? l?.unit_price ?? '';
export const defaultCost = l => (rawCost(l) === '' ? '' : Number(rawCost(l)).toFixed(2));   // what the field shows until edited
export const costOf = (l, unitCosts) => parseFloat(unitCosts?.[l.id] ?? rawCost(l)) || 0;   // what is posted

// 'over' (more than can be received) | 'negative' | null. Empty / 0 is fine: a line may be left out of a partial receipt.
export function lineIssue(l, qtyText) {
  const q = parseFloat(qtyText || 0);
  if (q < 0) return 'negative';
  return q > lineOpen(l) + EPS ? 'over' : null;
}

// receipt price vs the order price (the old "price differs" note + the cash funding-account rule)
export function priceDiff(l, unitCosts, qtyText) {
  const orderPrice = Number(rawCost(l)) || 0;
  const receiptPrice = Number(unitCosts?.[l.id] ?? defaultCost(l)) || 0;
  const diff = receiptPrice - orderPrice;
  if (!(Math.abs(diff) > 0.005)) return null;
  const delta = (parseFloat(qtyText || 0) || 0) * diff;
  return { orderPrice, receiptPrice, delta, up: diff > 0 };
}
export const anyPriceDiff = (lines, unitCosts, closeLines) => lines.some(l => !closeLines.has(l.id) && priceDiff(l, unitCosts, 0));
export const needsFunding = ({ isCredit, anyDiff, fundingRequired }) => (!isCredit && anyDiff) || !!fundingRequired;

// The posting plan, same split as the old panel: lines with qty > 0 (not marked 'cancel remaining') are received; marked lines are closed
// (close_short when something was already received, else close_without_receipt); with 'close short' the remainder of every received line is closed after it.
export function planReceipt({ lines, qtys, closeLines, shortClose }) {
  const normal = [], close = [];
  for (const line of lines) {
    if (closeLines.has(line.id)) close.push(line);
    else { const qty = parseFloat(qtys[line.id] || 0); if (qty > 0) normal.push({ line, qty }); }
  }
  const remainder = shortClose ? normal.filter(({ line, qty }) => num(line.qty_ordered) - num(line.qty_received) - qty > EPS) : [];
  return { normal, close, remainder, empty: normal.length === 0 && close.length === 0 };
}
export const closeKind = (line, extraQty = 0) => (num(line.qty_received) + extraQty > EPS ? { close_short: true } : { close_without_receipt: true });

export function receiveBody(line, qty, ctx) {
  const bird = isBirdLine(line);
  return { purchase_detail_id: line.id, receipt_date: ctx.receiptDate, quantity_received: qty, unit_cost: costOf(line, ctx.unitCosts),
    storage_id: bird ? null : (ctx.storageIds[line.id] || null), band_id: bird ? (line.band_id || null) : null,
    funding_account_id: ctx.fundingAccount || null, notes: ctx.notes || null, reason: ctx.reason || undefined };
}
export const closeBody = (line, kind, ctx) => ({ purchase_detail_id: line.id, receipt_date: ctx.receiptDate, quantity_received: 0, unit_cost: 0, ...kind, notes: ctx.notes || null, reason: ctx.reason || undefined });
export function previewBody(line, qty, ctx) {
  const bird = isBirdLine(line);
  return { preview: true, purchase_detail_id: line.id, receipt_date: ctx.receiptDate, quantity_received: qty, unit_cost: costOf(line, ctx.unitCosts),
    storage_id: bird ? null : (ctx.storageIds[line.id] || null), band_id: bird ? (line.band_id || null) : null, allocation_method: 'by_value', notes: ctx.notes || null };
}
export const closePreviewBody = (line, ctx) => ({ preview: true, purchase_detail_id: line.id, receipt_date: ctx.receiptDate, ...closeKind(line) });

// Soft notes (never blocking, never change a value): the receipt date against the order date / today, and PER LINE against its Order line ETA.
// kinds: future | beforeOrder | lineLate (the line's Order line ETA has passed today) | afterEta (this receipt is dated after the line's Order line ETA)
export function dateNotes({ receiptDate, orderDate, today, lines }) {
  const out = [];
  if (receiptDate && today && receiptDate > today) out.push({ kind: 'future' });
  if (receiptDate && orderDate && receiptDate < orderDate) out.push({ kind: 'beforeOrder', date: orderDate });
  for (const l of lines) {
    const eta = l.expected_receipt_date;
    const late = dayDiff(today, eta), after = dayDiff(receiptDate, eta);
    if (late != null && late > 0) out.push({ kind: 'lineLate', item: l.item_name, date: eta, days: late });
    if (after != null && after > 0) out.push({ kind: 'afterEta', item: l.item_name, date: eta, days: after });
  }
  return out;
}

// the order's delivery state for the head rail
export const railOf = (lines, receivable) => (!receivable ? 'received' : (lines || []).some(l => num(l.qty_received) > EPS) ? 'partial' : 'en_route');
